/**
 * features/finance/FeesSpecialCases.tsx
 * ------------------------------------------------------------------
 * One home for the fee EXCEPTIONS — the reasons a pupil is billed
 * differently from everyone else in their band. Sub-tabs rather than three
 * nav entries, because they are one job done three ways and the sidebar has
 * enough in it.
 *
 * THE RULE ALL THREE SHARE: whoever arranges a special case is not the one
 * who approves it. A finance manager proposes; the Head decides. Nothing
 * reaches a bill until it is approved — enforced at the billing feed and in
 * Firestore rules, not merely in these screens.
 *
 * STAFF CHILDREN IS DELIBERATELY NOT BUILT YET, and says so rather than
 * being quietly missing. The figure that screen would compute goes onto a
 * payslip, and computing it needs the school's remission rule — how many
 * children, at what percentage. That rule has not been confirmed, and the
 * sister system's version of it (first two free, next two at half) is that
 * school's policy, not this one's. A tab that guesses would be worse than a
 * tab that waits: HR can already record the deduction by hand, which is what
 * happens today.
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import { Users, CalendarCheck, Award, HeartHandshake, Info, type LucideIcon } from 'lucide-react';
import { PageHeader, SoftCard } from '@/features/finance/ui';
import FullAnnualPayment from '@/features/finance/FullAnnualPayment';
import Bursaries from '@/features/finance/Bursaries';

type SubTab = 'prepay' | 'bursary' | 'staff';

const TABS: { key: SubTab; label: string; icon: LucideIcon }[] = [
  { key: 'prepay', label: 'Full Annual Payment', icon: CalendarCheck },
  { key: 'bursary', label: 'Bursaries', icon: Award },
  { key: 'staff', label: 'Staff Children', icon: Users },
];

export default function FeesSpecialCases() {
  const [tab, setTab] = useState<SubTab>('prepay');

  return (
    <div>
      <PageHeader
        icon={HeartHandshake}
        title="Fee Special Cases"
        subtitle="Full annual payment · bursaries · staff children — proposed by finance, approved by the Head, every change audited"
      />

      <div className="flex flex-wrap gap-1 border-b border-gray-200 dark:border-gray-700 mb-5">
        {TABS.map((t) => {
          const Icon = t.icon;
          const on = t.key === tab;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
                on
                  ? 'border-primary text-primary dark:text-primarySoft'
                  : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'prepay' && <FullAnnualPayment />}
      {tab === 'bursary' && <Bursaries />}
      {tab === 'staff' && <StaffChildrenPending />}
    </div>
  );
}

/**
 * Stated plainly rather than left as an empty panel. Someone opening this tab
 * is looking for a thing they were told exists; the useful answer is what is
 * missing, why, and where the work happens meanwhile.
 */
function StaffChildrenPending() {
  return (
    <SoftCard>
      <div className="flex items-start gap-3">
        <Info size={20} className="text-primary dark:text-primarySoft mt-0.5 shrink-0" />
        <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
          <p className="font-medium text-gray-800 dark:text-gray-100">
            Not built yet — waiting on the school&rsquo;s staff fee remission rule.
          </p>
          <p>
            This screen will list each staff member&rsquo;s enrolled children, work out what they
            owe from the real fee structures, and push the net to payroll as a school-fees
            deduction. Pupils covered are then left out of the billing run, because the benefit is
            settled through payroll rather than by raising an invoice and discounting it away.
          </p>
          <p>
            What it needs first is the rule itself: how many children are covered and at what
            percentage. That figure lands on a payslip, so it is not something to infer.
          </p>
          <p className="text-gray-500 dark:text-gray-400">
            Until then, HR records the deduction by hand under
            {' '}<span className="font-medium">Payroll &rsaquo; Deductions</span>{' '}
            as &ldquo;School Fees &amp; Exams&rdquo;, exactly as it works today. Nothing has been
            taken away.
          </p>
        </div>
      </div>
    </SoftCard>
  );
}
